import { useCallback,useEffect,useState } from "react";
import { Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Slider,Typography } from "@mui/material";
import Cropper from "react-easy-crop";
import { useAppTheme } from "../../../context/ThemeContext";

const createImage=src=>new Promise((resolve,reject)=>{
  const image=new Image();
  image.onload=()=>resolve(image);
  image.onerror=reject;
  image.src=src;
});

const getCroppedFile=async(src,pixelCrop,originalName="imagen.jpg")=>{
  const image=await createImage(src);
  const canvas=document.createElement("canvas");
  const context=canvas.getContext("2d");

  canvas.width=Math.round(pixelCrop.width);
  canvas.height=Math.round(pixelCrop.height);

  context.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const blob=await new Promise((resolve,reject)=>{
    canvas.toBlob(
      value=>value?resolve(value):reject(new Error("No se pudo recortar la imagen.")),
      "image/jpeg",
      0.94
    );
  });

  const baseName=originalName.replace(/\.[^/.]+$/,"")||"imagen";

  return new File(
    [blob],
    `${baseName}-recortada.jpg`,
    {type:"image/jpeg"}
  );
};

export default function PhotoCropper({
  open,
  file,
  onClose,
  onConfirm,
  aspect=3/4,
  title="Ajustar fotografía",
  description="Mueve la imagen y ajusta el zoom para seleccionar la parte que se guardará.",
  confirmText="Usar fotografía"
}){
  const {colors,typography,shape}=useAppTheme();

  const [imageSrc,setImageSrc]=useState("");
  const [crop,setCrop]=useState({x:0,y:0});
  const [zoom,setZoom]=useState(1);
  const [croppedPixels,setCroppedPixels]=useState(null);
  const [processing,setProcessing]=useState(false);

  useEffect(()=>{
    if(!file){
      setImageSrc("");
      return;
    }

    const url=URL.createObjectURL(file);

    setImageSrc(url);
    setCrop({x:0,y:0});
    setZoom(1);
    setCroppedPixels(null);

    return ()=>URL.revokeObjectURL(url);
  },[file]);

  const handleCropComplete=useCallback((_,pixels)=>{
    setCroppedPixels(pixels);
  },[]);

  const handleConfirm=async()=>{
    if(!imageSrc||!croppedPixels||!file) return;

    try{
      setProcessing(true);

      const croppedFile=await getCroppedFile(
        imageSrc,
        croppedPixels,
        file.name
      );

      onConfirm(croppedFile);
    }finally{
      setProcessing(false);
    }
  };

  const isDocument=aspect>1;

  const cropSize=isDocument
    ?{width:500,height:312}
    :{width:300,height:400};

  return (
    <Dialog
      open={open}
      onClose={processing?undefined:onClose}
      fullWidth
      maxWidth="sm"
      disableScrollLock
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface
        }
      }}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:700,
            color:colors.text
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt:0.3,
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            position:"relative",
            width:"100%",
            height:440,
            backgroundColor:colors.primary,
            borderRadius:shape.radius,
            overflow:"hidden"
          }}
        >
          {imageSrc&&(
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropSize={cropSize}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </Box>

        <Box sx={{mt:2}}>
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.sm,
              fontWeight:600,
              color:colors.text
            }}
          >
            Zoom
          </Typography>

          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_,value)=>setZoom(value)}
            sx={{color:colors.controlActive}}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px:3,
          py:2,
          borderTop:`1px solid ${colors.border}`,
          gap:1
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          disabled={processing}
          sx={{
            color:colors.buttonText,
            textTransform:"none"
          }}
        >
          Cancelar
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleConfirm}
          disabled={processing||!croppedPixels}
          sx={{
            backgroundColor:colors.secondary,
            color:colors.buttonPrimaryText,
            boxShadow:"none",
            textTransform:"none",
            "&:hover":{
              backgroundColor:colors.secondaryHover,
              boxShadow:"none"
            }
          }}
        >
          {processing?"Procesando...":confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}